import React, { useState } from "react";

import ModeEditIcon from "@mui/icons-material/ModeEdit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  TableRow,
  TableCell,
  Box,
  Chip,
  Alert,
  Button,
  Link,
  Typography,
  Divider,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { getGroupMembers } from "@rubi/api-generated/clientPy";

import { PermissionStepper } from "./PermissionStepper";
import {
  AccessData,
  AccessSection,
  BulletinShareByBulletinItem,
} from "./types";
import { getAgencyBadge, formatExpiration, getGroupShareInfo } from "./utils";

interface TableSectionProps {
  title: string;
  data: AccessData[];
  section: AccessSection;
  showDivider?: boolean;
  searchTerm?: string;
  bulletinShares: BulletinShareByBulletinItem[];
  sharesFromCurrentUser: Set<number>;
  onExpireDateChange: (section: AccessSection, index: number | null) => void;
  onPermissionChange: (
    section: AccessSection,
    index: number | null,
    level: any,
  ) => void;
  onRevoke: (shareId: number) => void;
}

interface Moderator {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

const ModeratorsTooltip: React.FC<{ groupId: number | undefined }> = ({
  groupId,
}) => {
  const { moderators, loading, fetch } = useModerators(groupId);

  const tooltipContent = loading ? (
    <CircularProgress size={12} color="inherit" />
  ) : moderators && moderators.length > 0 ? (
    <Box>
      {moderators.map((m) => (
        <div key={m.id}>
          {m.first_name} {m.last_name} — {m.email}
        </div>
      ))}
    </Box>
  ) : moderators?.length === 0 ? (
    <span>No moderators assigned</span>
  ) : null;

  return (
    <Tooltip title={tooltipContent} onOpen={fetch} arrow>
      <span style={{ textDecoration: "underline dotted", cursor: "help" }}>
        moderators
      </span>
    </Tooltip>
  );
};

function useModerators(groupId: number | undefined) {
  const [moderators, setModerators] = useState<Moderator[] | null>(null);
  const [loading, setLoading] = useState(false);

  const fetch = () => {
    if (groupId == null || moderators !== null || loading) return;
    setLoading(true);
    getGroupMembers({
      path: { group_id: groupId },
      query: { permission: "moderate_bulletin_admittance" },
    })
      .then(({ data }) => setModerators((data as Moderator[]) ?? []))
      .catch(() => setModerators([]))
      .finally(() => setLoading(false));
  };

  return { moderators, loading, fetch };
}

export const TableSection: React.FC<TableSectionProps> = ({
  title,
  data,
  section,
  showDivider = true,
  searchTerm,
  bulletinShares,
  sharesFromCurrentUser,
  onExpireDateChange,
  onPermissionChange,
  onRevoke,
}) => {
  const getMaxPermission = (row: AccessData) => {
    // if the user is the owner and section is `me`, then max permission is 3
    if (row.isOwner && section === "me") {
      return 2;
    }
    return 1;
  };

  const getRowBadges = (row: AccessData) => {
    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: { xs: 0.25, sm: 0.5 },
        }}
      >
        {row.isOwner && (
          <Chip
            label="Owner"
            size="small"
            variant="outlined"
            color="primary"
            sx={{ fontSize: { xs: "0.6rem", sm: "0.75rem" } }}
          />
        )}
        {row.isPublic && (
          <Tooltip title="Open Public Bulletin Board">
            <Chip
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  Public
                  <OpenInNewIcon
                    sx={{
                      fontSize: { xs: "0.7rem", sm: "0.8rem" },
                      cursor: "pointer",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(
                        `/rubialert/public/group/${row.id}`,
                        "_blank",
                      );
                    }}
                  />
                </Box>
              }
              size="small"
              variant="filled"
              color="success"
              sx={{
                fontWeight: "bold",
                fontSize: { xs: "0.6rem", sm: "0.75rem" },
                cursor: "pointer",
              }}
              onClick={() =>
                window.open(`/rubialert/public/group/${row.id}`, "_blank")
              }
            />
          </Tooltip>
        )}
        {row.isPoliceService && (
          <Chip
            label="Police Service"
            size="small"
            variant="outlined"
            color="info"
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "0.6rem", sm: "0.75rem" },
            }}
          />
        )}
        {row.requires_admittance_moderation && (
          <Chip
            label="Requires Approval"
            size="small"
            variant="outlined"
            color="warning"
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "0.6rem", sm: "0.75rem" },
            }}
          />
        )}
      </Box>
    );
  };
  return (
    <>
      {title && (
        <TableRow>
          <TableCell
            colSpan={5}
            sx={{ backgroundColor: "action.hover", fontWeight: "bold" }}
          >
            {title}
          </TableCell>
        </TableRow>
      )}
      {data.map((row, index) => {
        const shareInfo = row.id
          ? getGroupShareInfo(row.id, bulletinShares, sharesFromCurrentUser)
          : {
              hasShares: false,
              hasSharesFromCurrentUser: false,
              shareFromCurrentUser: null,
              allShares: [],
            };
        return (
          <TableRow key={`${section}-${row.id || index}`}>
            {/* {JSON.stringify(row)} */}
            <TableCell>
              {getAgencyBadge(row.agency)}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xs: 0.5, sm: 1 },
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                }}
              >
                <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
                  {section === "my_groups" ? (
                    <Tooltip title="See members of the group">
                      <Link href={`/rubialert/manage-groups/${row.id}`}>
                        {row.name}
                      </Link>
                    </Tooltip>
                  ) : (
                    <Tooltip title="See members of the group">
                      <span>{row.name}</span>
                    </Tooltip>
                  )}
                </Box>
              </Box>
              {getRowBadges(row)}
            </TableCell>
            <TableCell>
              <Box>
                <PermissionStepper
                  canView={row.canView}
                  canModify={row.canModify}
                  onPermissionChange={(level) =>
                    onPermissionChange(
                      section,
                      section === "me" ? null : index,
                      level,
                    )
                  }
                  maxPermission={getMaxPermission(row)}
                  canShareToGroup={row.canShareToGroup}
                  canShareReason={row.canShareReason}
                  canRemoveFromBulletin={row.canRemoveFromBulletin}
                  canRemoveReason={row.canRemoveReason}
                  hasPendingShare={row.hasPendingShare}
                  isPublic={row.isPublic}
                />

                {/* Show warning for shares from current user */}
                {shareInfo.hasSharesFromCurrentUser &&
                  shareInfo.shareFromCurrentUser && (
                    <Alert
                      severity="warning"
                      variant="outlined"
                      sx={{ mt: 1 }}
                      icon={false}
                    >
                      Sharing request sent to the group&apos;s{" "}
                      <ModeratorsTooltip groupId={row.id} /> for review; Once
                      approved, bulletin will be visible to users in the group.
                      <Box sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() =>
                            onRevoke(
                              shareInfo.shareFromCurrentUser!.bulletin_share.id,
                            )
                          }
                        >
                          Cancel Sharing Request
                        </Button>
                      </Box>
                    </Alert>
                  )}

                {/* Show info for shares from other users */}
                {shareInfo.hasShares && !shareInfo.hasSharesFromCurrentUser && (
                  <Alert severity="info" variant="outlined">
                    This bulletin has been shared to this group by another user.
                  </Alert>
                )}

                {/* Legacy pending share logic */}
                {row.hasPendingShare && !shareInfo.hasShares && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "warning.main",
                      display: "block",
                      mt: 1,
                      fontStyle: "italic",
                    }}
                  >
                    Permission cannot be changed while there is a pending share
                    request,{" "}
                    <Link
                      component="button"
                      variant="caption"
                      onClick={() => onRevoke(row.bulletinShareId!)}
                      sx={{
                        color: "error.main",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontSize: "inherit",
                        fontStyle: "italic",
                      }}
                    >
                      Revoke
                    </Link>
                  </Typography>
                )}
              </Box>
            </TableCell>
            <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title="Users with access can share with any other group.">
                  <div
                    style={{
                      fontSize: "0.75rem",
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <Chip
                      label={row.reshareScope || "-"}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </div>
                </Tooltip>
                {/* {currentUserCanReshare &&
                  section !== "me" &&
                  (row.canView || row.canModify) && (
                    <IconButton
                      size="small"
                      onClick={() => handleOpenReshareScopeDialog(row)}
                      sx={{
                        color: "text.secondary",
                        p: { xs: 0.25, sm: 0.5 },
                        "&:hover": {
                          color: "primary.main",
                        },
                      }}
                    >
                      <SettingsIcon fontSize="small" />
                    </IconButton>
                  )} */}
              </Box>
            </TableCell>
            <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  component="div"
                  variant="body2"
                  sx={{
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {formatExpiration(
                    row.time_expires,
                    row.canView || row.canModify,
                  )}
                </Typography>
                {section !== "me" && (row.canView || row.canModify) && (
                  <Tooltip
                    title={
                      row.canRemoveFromBulletin === false && row.canRemoveReason
                        ? row.canRemoveReason
                        : "Edit expiration date"
                    }
                    placement="top"
                    arrow
                  >
                    <span>
                      <ModeEditIcon
                        sx={{
                          fontSize: { xs: 14, sm: 16 },
                          color:
                            row.canRemoveFromBulletin === false
                              ? "text.disabled"
                              : "text.secondary",
                          cursor:
                            row.canRemoveFromBulletin === false
                              ? "not-allowed"
                              : "pointer",
                          "&:hover": {
                            color:
                              row.canRemoveFromBulletin === false
                                ? "text.disabled"
                                : "primary.main",
                          },
                          pointerEvents:
                            row.canRemoveFromBulletin === false
                              ? "none"
                              : "auto",
                        }}
                        onClick={() => {
                          if (row.canRemoveFromBulletin) {
                            onExpireDateChange(section, index);
                          }
                        }}
                      />
                    </span>
                  </Tooltip>
                )}
              </Box>
            </TableCell>
          </TableRow>
        );
      })}
      {data.length === 0 && section === "search_results" && (
        <TableRow>
          <TableCell
            colSpan={5}
            sx={{
              textAlign: "center",
              py: 2,
              color: "text.secondary",
              fontStyle: "italic",
            }}
          >
            {searchTerm
              ? "No groups found matching your search"
              : "Enter a search term to find groups"}
          </TableCell>
        </TableRow>
      )}
      {showDivider && (
        <TableRow>
          <TableCell colSpan={5} sx={{ padding: 0 }}>
            <Divider />
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
